"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  ColorSwatches,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT, LABEL } from "@/components/tool-kit/styles";
import { FishboneDiagram } from "@/components/tools/diagrams-more/fishbone-diagram";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { type ChartColorId, colorIndex } from "@/lib/tool-kit/charts/color";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { diagramColor } from "@/lib/tool-kit/diagrams/color";
import {
  addCategories,
  type FishWarning,
  fishCounts,
  fishOutline,
  fishRows,
  layoutFishbone,
  MAX_CATEGORIES,
  MAX_CAUSES,
  parseFishbone,
} from "@/lib/tool-kit/diagrams-more/fishbone";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

/** English strings. A translated content file overrides these through `ui`. */
export const FISHBONE_DIAGRAM_MAKER_STRINGS = {
  inputLabel:
    "Your fishbone — the effect first, then categories, then causes, indented",
  inputHint:
    "Line one is the effect (the problem, in the head of the fish). Lines indented under it are the categories; lines indented under a category are its causes, and a further indent makes a sub-cause. Indent with spaces or tabs; bullets and numbers are removed, and a trailing colon on a category is ignored.",
  exampleText:
    "Effect: Customer orders arrive late\nMethods\n  No standard packing routine\n    Each shift packs in its own order\n  Orders held until the truck is full\nMachines\n  Label printer jams\n  Scanner battery fails mid-shift\nMaterials\n  Boxes out of stock\n  Wrong sizes on shelf labels\nPeople\n  New staff untrained on the system\n  Overtime needs late approval\nMeasurement\n  Delivery time recorded only at dispatch\nEnvironment\n  Winter road closures\n  Dock too small for two trucks",
  exampleTitle: "Why Customer Orders Arrive Late",
  effectPlaceholder: "Effect: the problem you are explaining",
  /** Words that may label the first line, with ":" or "：". English ones always work. */
  effectPrefixes: ["Effect", "Problem", "Head"],
  loadExample: "Load the example",
  clear: "Clear",
  suggestionsLabel: "Category suggestions (add to the outline, then edit)",
  suggestionsHint:
    "Starting points only — every set has variants, and the best categories are the ones that fit your problem.",
  setSixMs: "6 Ms (manufacturing)",
  setEightPs: "8 Ps (marketing and services)",
  setFourSs: "4 Ss (service)",
  sixMsNames: {
    man: "Man (people)",
    machine: "Machine",
    method: "Method",
    material: "Material",
    measurement: "Measurement",
    nature: "Mother Nature (environment)",
  },
  eightPsNames: {
    product: "Product",
    price: "Price",
    place: "Place",
    promotion: "Promotion",
    people: "People",
    process: "Process",
    physical: "Physical evidence",
    performance: "Performance",
  },
  fourSsNames: {
    surroundings: "Surroundings",
    suppliers: "Suppliers",
    systems: "Systems",
    skills: "Skills",
  },
  categoriesDetected: "one:{count} category|other:{count} categories",
  causesDetected: "one:{count} cause|other:{count} causes",
  subCausesDetected: "one:{count} sub-cause|other:{count} sub-causes",
  enterEffect: "Type the effect on the first line to start the diagram.",
  noCategories:
    "Add categories on the lines below the effect, indented, to grow the bones.",
  warnings: {
    tooManyCategories:
      "“{text}” was left out — the diagram holds up to {limit} categories.",
    tooManyCauses:
      "Some causes under “{text}” were left out — a category holds up to {limit}.",
    dropped: "Some lines were left out because the outline is very long.",
  },
  categoryColumn: "Category",
  levelColumn: "Level",
  causeColumn: "Cause",
  levelCause: "Cause",
  levelSubCause: "Sub-cause",
  oneColor: "Draw every bone in one color",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title (optional)",
  colorLabel: "Color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  firstColor: "Color of the first category (the rest follow in order)",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy as outline",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the diagram as a table",
  chartLabel: "Fishbone diagram",
  chartDescription:
    "Fishbone diagram of the effect “{effect}” with {categories}: {list}.",
} as const;

function levelName(
  strings: typeof FISHBONE_DIAGRAM_MAKER_STRINGS,
  level: 0 | 1,
  text: string
): string {
  if (text === "") {
    return "";
  }
  return level === 0 ? strings.levelCause : strings.levelSubCause;
}

function warningText(
  strings: typeof FISHBONE_DIAGRAM_MAKER_STRINGS,
  warning: FishWarning,
  limits: { limit: string }
): string {
  if (warning.kind === "dropped") {
    return strings.warnings.dropped;
  }
  return t(strings.warnings[warning.kind], {
    text: warning.text,
    limit: limits.limit,
  });
}

export default function FishboneDiagramMakerWidget() {
  const strings = useToolUi(FISHBONE_DIAGRAM_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleText);
  const [single, setSingle] = useState(false);
  const [colorId, setColorId] = useState<ChartColorId>("blue");
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const parse = parseFishbone(raw, strings.effectPrefixes);
  const hasEffect = parse.effect !== "";
  const layout = hasEffect ? layoutFishbone(parse) : null;
  const counts = fishCounts(parse);
  const start = colorIndex(colorId);
  const colors = parse.categories.map((_, index) =>
    single ? CHART_COLORS[start] : diagramColor(start, index)
  );
  const rows = fishRows(parse).map((row) => [
    row.category,
    levelName(strings, row.level, row.text),
    row.text,
  ]);
  const categoriesText = tPlural(
    strings.categoriesDetected,
    locale,
    counts.categories,
    {
      count: fmt(counts.categories),
    }
  );
  const suggestions = [
    { label: strings.setSixMs, names: Object.values(strings.sixMsNames) },
    { label: strings.setEightPs, names: Object.values(strings.eightPsNames) },
    { label: strings.setFourSs, names: Object.values(strings.fourSsNames) },
  ];

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="fdm-input"
        label={strings.inputLabel}
        onChange={setRaw}
        rows={14}
        value={raw}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRaw(strings.exampleText)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRaw("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div className="space-y-2">
        <p className={LABEL}>{strings.suggestionsLabel}</p>
        <div className="flex flex-wrap gap-3">
          {suggestions.map((suggestion) => (
            <button
              className={BUTTON_SECONDARY}
              key={suggestion.label}
              onClick={() =>
                setRaw(
                  addCategories(
                    raw,
                    suggestion.names,
                    strings.effectPlaceholder,
                    strings.effectPrefixes
                  )
                )
              }
              type="button"
            >
              {suggestion.label}
            </button>
          ))}
        </div>
        <p className={HINT}>{strings.suggestionsHint}</p>
      </div>
      <div aria-live="polite" className="space-y-2">
        {hasEffect ? (
          <p className={HINT}>
            {categoriesText}
            {" · "}
            {tPlural(strings.causesDetected, locale, counts.causes, {
              count: fmt(counts.causes),
            })}
            {counts.subCauses > 0
              ? ` · ${tPlural(strings.subCausesDetected, locale, counts.subCauses, { count: fmt(counts.subCauses) })}`
              : ""}
          </p>
        ) : (
          <Notice>{strings.enterEffect}</Notice>
        )}
        {hasEffect && counts.categories === 0 ? (
          <Notice>{strings.noCategories}</Notice>
        ) : null}
        {parse.warnings.map((warning) => (
          <Notice key={`${warning.kind}-${warning.text}`}>
            {warningText(strings, warning, {
              limit: fmt(
                warning.kind === "tooManyCauses" ? MAX_CAUSES : MAX_CATEGORIES
              ),
            })}
          </Notice>
        ))}
      </div>
      {layout ? (
        <ChartFigure
          apa={style.apa}
          data={fishOutline(parse)}
          figureNumber={style.figureNumber}
          filename={chartFilename(
            style.title || parse.effect,
            "fishbone-diagram"
          )}
          labels={strings}
          note={style.note}
          table={{
            head: [
              strings.categoryColumn,
              strings.levelColumn,
              strings.causeColumn,
            ],
            rows,
          }}
          title={style.title}
        >
          <FishboneDiagram
            colors={colors}
            description={t(strings.chartDescription, {
              effect: parse.effect,
              categories: categoriesText,
              list: parse.categories
                .map((category) => category.name)
                .join("; "),
            })}
            id="fdm-chart"
            label={style.title.trim() || strings.chartLabel}
            layout={layout}
            title={style.apa ? "" : style.title}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        idPrefix="fdm"
        labels={strings}
        style={style}
        update={update}
      >
        <Checkbox
          checked={single}
          label={strings.oneColor}
          onChange={setSingle}
        />
        <ColorSwatches
          labels={strings}
          legend={strings.firstColor}
          name="fdm-color"
          onChange={setColorId}
          value={colorId}
        />
      </ChartStyleFields>
    </div>
  );
}
